import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Textarea,
  useToast,
  VStack,
} from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'
import {
  MaintenanceFee,
  MaintenanceFeeInput,
  useCreateMaintenanceFee,
  useUpdateMaintenanceFee,
} from '../../../../hooks/useMaintenanceFees'

interface MaintenanceFeeFormProps {
  isOpen: boolean
  onClose: () => void
  propertyId: string
  editingFee?: MaintenanceFee | null
}

interface FormData {
  repair_date: string
  cost: number
  description: string
}

interface FormErrors {
  repair_date?: string
  cost?: string
}

export function MaintenanceFeeForm({
  isOpen,
  onClose,
  propertyId,
  editingFee,
}: MaintenanceFeeFormProps) {
  const createMaintenanceFee = useCreateMaintenanceFee()
  const updateMaintenanceFee = useUpdateMaintenanceFee()
  const toast = useToast()

  const [formData, setFormData] = useState<FormData>({
    repair_date: '',
    cost: 0,
    description: '',
  })

  const [errors, setErrors] = useState<FormErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const isEditing = !!editingFee

  useEffect(() => {
    if (isOpen) {
      if (editingFee) {
        // Populate form with editing data
        setFormData({
          repair_date: editingFee.repair_date,
          cost: editingFee.cost,
          description: editingFee.description || '',
        })
      } else {
        // Reset form for new entry
        setFormData({
          repair_date: '',
          cost: 0,
          description: '',
        })
      }
      setErrors({})
    }
  }, [isOpen, editingFee])

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    if (!formData.repair_date) {
      newErrors.repair_date = 'Data do reparo é obrigatória'
    }

    if (!formData.cost || formData.cost <= 0) {
      newErrors.cost = 'Custo deve ser maior que zero'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)

    try {
      const submitData: MaintenanceFeeInput = {
        property_id: propertyId,
        repair_date: formData.repair_date,
        cost: formData.cost,
        description: formData.description,
      }

      if (isEditing && editingFee) {
        await updateMaintenanceFee.mutateAsync({
          id: editingFee.id,
          data: {
            repair_date: submitData.repair_date,
            cost: submitData.cost,
            description: submitData.description,
          },
        })
      } else {
        await createMaintenanceFee.mutateAsync(submitData)
      }

      handleClose()
    } catch (error) {
      console.error('Error submitting maintenance fee:', error)
      toast({
        title: 'Erro ao salvar',
        description: 'Ocorreu um erro ao salvar a taxa de manutenção',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setFormData({
      repair_date: '',
      cost: 0,
      description: '',
    })
    setErrors({})
    setIsSubmitting(false)
    onClose()
  }

  const handleInputChange = (field: keyof FormData, value: string | number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))

    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }))
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <form onSubmit={handleSubmit}>
          <ModalHeader>
            {isEditing
              ? 'Editar Taxa de Manutenção'
              : 'Nova Taxa de Manutenção'}
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <VStack spacing={4}>
              <FormControl isInvalid={!!errors.repair_date} isRequired>
                <FormLabel>Data do Reparo</FormLabel>
                <Input
                  type="date"
                  value={formData.repair_date}
                  onChange={(e) =>
                    handleInputChange('repair_date', e.target.value)
                  }
                />
                <FormErrorMessage>{errors.repair_date}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.cost} isRequired>
                <FormLabel>Custo (R$)</FormLabel>
                <NumberInput
                  min={0}
                  precision={2}
                  step={0.01}
                  value={formData.cost}
                  onChange={(valueString) => {
                    const value = parseFloat(valueString) || 0
                    handleInputChange('cost', value)
                  }}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormErrorMessage>{errors.cost}</FormErrorMessage>
              </FormControl>

              <FormControl>
                <FormLabel>Descrição</FormLabel>
                <Textarea
                  placeholder="Descreva o tipo de reparo ou manutenção realizada..."
                  rows={4}
                  value={formData.description}
                  onChange={(e) =>
                    handleInputChange('description', e.target.value)
                  }
                />
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              colorScheme="blue"
              isLoading={isSubmitting}
              loadingText={isEditing ? 'Atualizando...' : 'Criando...'}
            >
              {isEditing ? 'Atualizar' : 'Criar'}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}
